import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FaCog, FaFileExcel } from 'react-icons/fa';
import Axios from 'axios';

import { FormattedMessage, injectIntl } from 'react-intl';

import intlShape from '@/shared/utils/intlShape';
import { injectStateService, StateService } from '@/shared/services/StateService';
import api from '@/shared/api';
import config from '@/config';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import { listItemMsg, formatListValue } from '@/shared/components/FormattedListValue';
import RelationName from '@/shared/components/RelationName';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import Checkbox from '@/shared/components/input/Checkbox';
import DeviationOverviewGlobalActionsButton from './DeviationOverviewGlobalActionsButton';
import BulkUpdateDeviationsModal from './BulkUpdateDeviationsModal';
import guid from '@/shared/utils/guid';
import userStorage from '@/shared/services/userStorage';
import Button from '@/shared/components/Button';
import SimplePanel from '@/shared/components/SimplePanel';
import handleHttpError from '@/shared/services/HandleHttpError';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';

import { showWhenPermission } from '@/shared/components/security';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import { minutesToTimeSpan } from '@/shared/utils/adminpulse';

import DeviationOverviewFilters, { defaultFilters } from './DeviationOverviewFilters';
import messages from './deviationOverviewMessages';

const columnSelectionStorageKey = 'DeviationOverviewColumns';
const paramsStorageKey = 'DeviationOverviewParams';

const getTaskTemplateColumnFilters = filter =>
  Object.keys(filter)
    .filter(key => key.startsWith('tasktemplate_'))
    .reduce(
      (obj, key) => ({
        ...obj,
        [key.substr(13)]: filter[key].items.map(x => x.id),
      }),
      {},
    );

// deviation_<templateId> -> 1 (with deviation) | 2 (without deviation).
// Selecting both options (or none) means "all" and is omitted.
const getColumnDeviationFilters = filter =>
  Object.keys(filter)
    .filter(key => key.startsWith('deviation_'))
    .reduce((obj, key) => {
      const items = (filter[key] && filter[key].items) || [];
      if (items.length !== 1) return obj;
      return { ...obj, [key.substr('deviation_'.length)]: items[0].id };
    }, {});

class DeviationOverviewContainer extends Component {
  state = {
    showColumnSelectionModal: false,
    showBulkModal: false,
    selected: {},
    exporting: false,
    items: [],
    taskTemplates: [],
    subtaskColumns: null,
    initialSearchParams: {
      page: 0,
      pageSize: 50,
      sortKey: 'relation',
      desc: false,
      columns: [],
      overviewFilters: {
        ...defaultFilters,
      },
    },
  };

  constructor(props) {
    super(props);

    const { initialSearchParams: orgSearchParams } = this.state;

    const { overviewFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem(paramsStorageKey)) || {};
    const selectedColumns = userStorage.getItem(columnSelectionStorageKey) || [];

    this.state.initialSearchParams = {
      ...orgSearchParams,
      ...sessionParams,
      overviewFilters: { ...defaultFilters, ...overviewFilters },
      columns: selectedColumns,
    };
  }

  componentDidMount() {
    const { intl } = this.props;
    const endpoint = `${config.appUrls.tasks}/api/TaskTemplates/Schedules/`;
    api.base.get(endpoint).then(({ data }) => {
      const { initialSearchParams: { columns } = {} } = this.state;

      const taskTemplates = data.map(x => ({
        ...x,
        label: intl.formatMessage(listItemMsg('TaskTemplate', x.id, x.name)),
        selected: columns.length === 0 || columns.indexOf(x.id) >= 0,
      }));

      this.setState({ taskTemplates });
    });
    this.withoutScheduleFilterItem = {
      id: guid.empty,
      name: intl.formatMessage(messages.WithoutSchedule),
    };
    this.deviationFilterItems = [
      { id: 1, name: intl.formatMessage(messages.WithDeviation) },
      { id: 2, name: intl.formatMessage(messages.WithoutDeviation) },
    ];
  }

  componentWillUnmount() {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  loadItems = searchParams => {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();

    const filter = searchParams.filter || {};
    const { intl } = this.props;
    const mainTaskTemplateId = searchParams.overviewFilters?.mainTaskTemplateId;

    const taskTemplateFilters = getTaskTemplateColumnFilters(filter);

    const endpoint = `${config.appUrls.tasks}/api/CapacityPlanning/DeviationOverview`;
    sessionStorage.setItem(
      paramsStorageKey,
      JSON.stringify({
        overviewFilters: searchParams.overviewFilters,
        sortKey: searchParams.sortKey,
        desc: searchParams.desc,
        filter,
      }),
    );

    return api.base
      .post(
        endpoint,
        {
          relationId: searchParams.overviewFilters?.relationId || undefined,
          mainTaskTemplateId: mainTaskTemplateId || undefined,
          ownCompanies: searchParams.overviewFilters?.ownCompanies || [],
          teams: searchParams.overviewFilters?.teams || [],
          legalEntities: searchParams.overviewFilters?.legalEntities || [],
          withLabels: searchParams.overviewFilters?.withLabels || [],
          assignmentTemplateId: searchParams.overviewFilters?.assignmentTemplateId || undefined,
          page: searchParams.page,
          take: searchParams.pageSize,
          sort: searchParams.sortKey,
          desc: searchParams.desc,
          columns: searchParams.columns || [],
          periodicityFilters: taskTemplateFilters,
          columnDeviationFilters: getColumnDeviationFilters(filter),
        },
        { cancelToken: this.cancelToken.token },
      )
      .then(({ data }) => {
        if (!data) return;

        const newItems = data.rows.map(e => ({ ...e }));
        this.setState(state => ({
          ...state,
          items: searchParams.page > 0 ? [...state.items, ...newItems] : [...newItems],
          totalRows: searchParams.page === 0 ? data.totalRows : state.totalRows,
          subtaskColumns: data.subtaskColumns || null,
          initialSearchParams: {
            ...state.initialSearchParams,
            filter: searchParams.filter,
            sortKey: searchParams.sortKey,
            desc: searchParams.desc,
          },
        }));
        return data.rows;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) {
          return handleHttpError(intl)(error);
        }
      });
  };

  handleColumnSelection = items => {
    let selectedColumns = items.filter(x => x.selected).map(x => x.id);
    if (selectedColumns.length === items.length) selectedColumns = [];
    userStorage.setItem(columnSelectionStorageKey, selectedColumns);

    this.setState(state => ({
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        columns: selectedColumns,
      },
      taskTemplates: [...items],
      showColumnSelectionModal: false,
    }));
  };

  getMainTaskColumns = () => {
    const { taskTemplates, initialSearchParams: { columns = [] } = {} } = this.state;
    return columns.length ? taskTemplates.filter(x => columns.indexOf(x.id) >= 0) : taskTemplates;
  };

  getVisibleColumns = () => {
    const { subtaskColumns, taskTemplates } = this.state;
    const { intl } = this.props;
    const mainTaskTemplateId = this.state.initialSearchParams.overviewFilters?.mainTaskTemplateId;

    if (mainTaskTemplateId && subtaskColumns) {
      const mainTemplate = taskTemplates.find(t => t.id === mainTaskTemplateId);
      return subtaskColumns.map(c => ({
        id: c.id,
        label: intl.formatMessage(listItemMsg('TaskTemplate', c.id, c.name)),
        isMainTask: c.isMainTask,
        periodicities: c.isMainTask && mainTemplate ? mainTemplate.periodicities : undefined,
      }));
    }
    return this.getMainTaskColumns();
  };

  filtersChanged = filters => {
    this.setState(state => {
      const currentMainTask = state.initialSearchParams.overviewFilters?.mainTaskTemplateId;
      const mainTaskChanged = filters.mainTaskTemplateId !== currentMainTask;
      return {
        selected: {},
        initialSearchParams: {
          ...state.initialSearchParams,
          page: 0,
          overviewFilters: filters,
          ...(mainTaskChanged ? { filter: {} } : {}),
        },
      };
    });
  };

  toggleSelected = id => {
    this.setState(state => ({ selected: { ...state.selected, [id]: !state.selected[id] } }));
  };

  toggleSelectAll = () => {
    const { items, selected } = this.state;
    const allSelected = items.length > 0 && items.every(i => selected[i.id]);
    const next = {};
    if (!allSelected)
      items.forEach(i => {
        next[i.id] = true;
      });
    this.setState({ selected: next });
  };

  getSelectedIds = () => {
    const { selected } = this.state;
    return Object.keys(selected).filter(id => selected[id]);
  };

  onBulkSuccess = job => {
    const { intl, stateService } = this.props;
    const onClick = job && job.id ? () => stateService.gotoJob(job.id) : undefined;
    ToastrService.info(intl.formatMessage(messages.BulkUpdateStarted), undefined, onClick);
    this.setState({ selected: {}, showBulkModal: false });
  };

  createExcelReport = () => {
    const { intl } = this.props;
    const { initialSearchParams } = this.state;
    const filter = initialSearchParams.filter || {};
    const taskTemplateFilters = getTaskTemplateColumnFilters(filter);
    const overviewFilters = initialSearchParams.overviewFilters || {};

    const body = {
      relationId: overviewFilters.relationId || undefined,
      mainTaskTemplateId: overviewFilters.mainTaskTemplateId || undefined,
      ownCompanies: overviewFilters.ownCompanies || [],
      legalEntities: overviewFilters.legalEntities || [],
      teams: overviewFilters.teams || [],
      withLabels: overviewFilters.withLabels || [],
      assignmentTemplateId: overviewFilters.assignmentTemplateId || undefined,
      columns: initialSearchParams.columns || [],
      periodicityFilters: taskTemplateFilters,
      columnDeviationFilters: getColumnDeviationFilters(filter),
      sort: initialSearchParams.sortKey,
      desc: initialSearchParams.desc,
    };
    const culture = intl.locale;
    const connectionId = SignalRService.getConnectionId();

    this.setState({ exporting: true });

    return api.base
      .post(
        `${
          config.appUrls.tasks
        }/api/CapacityPlanning/DeviationOverviewExport?culture=${encodeURIComponent(
          culture,
        )}&connectionId=${encodeURIComponent(connectionId)}`,
        body,
      )
      .then(() => {
        this.setState({ exporting: false });
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        this.setState({ exporting: false });
        handleHttpError(intl)(error);
      });
  };

  formatDuration = cell => {
    if (!cell || cell.effectiveDuration == null) return '-';
    return minutesToTimeSpan(cell.effectiveDuration);
  };

  renderRow = item => {
    const columns = this.getVisibleColumns();
    const showAssignments = hasAssignments();
    const { intl } = this.props;

    return (
      <tbody className="hover" key={item.id}>
        <tr>
          <td>
            <Checkbox
              value={!!this.state.selected[item.id]}
              onClick={() => this.toggleSelected(item.id)}
            />
          </td>
          <td>
            <RelationName relation={item.relation} />
          </td>
          {showAssignments && <td className="fw-normal">{mapInvoiceStreamName(item, intl)}</td>}
          {columns.map(col => {
            const cell = item.columns[col.id];
            const isDefault = cell && !cell.isDeviation;
            return (
              <td key={col.id} className="text-right">
                <span
                  style={isDefault ? { fontStyle: 'italic' } : undefined}
                  title={
                    cell && cell.isDeviation
                      ? `Default: ${
                          cell.defaultDuration != null
                            ? minutesToTimeSpan(cell.defaultDuration)
                            : '-'
                        }, Deviation: ${
                          cell.deviationDuration != null
                            ? minutesToTimeSpan(cell.deviationDuration)
                            : '-'
                        }`
                      : undefined
                  }
                >
                  {this.formatDuration(cell)}
                </span>
              </td>
            );
          })}
        </tr>
      </tbody>
    );
  };

  render() {
    const {
      initialSearchParams,
      items,
      taskTemplates,
      showColumnSelectionModal,
      showBulkModal,
      selected,
    } = this.state;
    const { intl } = this.props;
    const columns = this.getVisibleColumns();
    const mainTaskTemplateId = initialSearchParams.overviewFilters?.mainTaskTemplateId;

    const showAssignments = hasAssignments();
    const selectedIds = this.getSelectedIds();
    const allSelected = items.length > 0 && items.every(i => selected[i.id]);

    return (
      <div>
        <div className="fixed-filters">
          <SimplePanel
            title={intl.formatMessage(messages.DeviationOverview)}
            article="27849775459740"
          >
            <DeviationOverviewFilters
              filters={initialSearchParams.overviewFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
            />
          </SimplePanel>
          <div className="row btn-row">
            <div className="col-md-12 pull-right content-right">
              <DeviationOverviewGlobalActionsButton
                disabled={selectedIds.length === 0}
                onUpdateExecutionDuration={() => this.setState({ showBulkModal: true })}
              />
              <Button
                btnClassName="btn btn-xs btn-default margin10R"
                onClick={this.createExcelReport}
                disabled={this.state.exporting}
              >
                <span className="margin5R">
                  <FaFileExcel className="file-icon" />
                </span>
                <FormattedMessage {...messages.ExportToExcel} />
              </Button>
              {taskTemplates && taskTemplates.length > 0 && !mainTaskTemplateId && (
                <Button
                  btnClassName="btn btn-xs btn-default margin10R"
                  onClick={() => this.setState({ showColumnSelectionModal: true })}
                >
                  <span className="margin5R">
                    <FaCog className="react-icon" />
                  </span>
                  <FormattedMessage {...messages.ColumnConfig} />
                </Button>
              )}
            </div>
          </div>
        </div>

        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => this.renderRow(item)}
          noDataLabel={<FormattedMessage {...messages.NoDataFound} />}
        >
          <ColumnHeader title={<Checkbox value={allSelected} onClick={this.toggleSelectAll} />} />
          <ColumnHeader title={intl.formatMessage(messages.Relation)} sortKey="relation" />
          {showAssignments ? (
            <ColumnHeader title={intl.formatMessage(messages.Assignment)} />
          ) : null}

          {columns &&
            columns.map(col => {
              const periodicityFilter = col.periodicities
                ? {
                    type: 'items',
                    key: `tasktemplate_${col.id}`,
                    items: [this.withoutScheduleFilterItem, ...col.periodicities],
                    renderItem: item =>
                      formatListValue({
                        id: item.id,
                        list: 'taskperiodicity',
                        fallback: item.name,
                        intl,
                      }),
                  }
                : undefined;

              const deviationFilter = mainTaskTemplateId
                ? {
                    type: 'items',
                    key: `deviation_${col.id}`,
                    items: this.deviationFilterItems,
                    renderItem: item => item.name,
                  }
                : undefined;

              if (periodicityFilter && deviationFilter) {
                return (
                  <ColumnHeader
                    key={col.id}
                    title={col.label}
                    className="text-right"
                    filters={[periodicityFilter, deviationFilter]}
                  />
                );
              }

              return (
                <ColumnHeader
                  key={col.id}
                  title={col.label}
                  className="text-right"
                  filter={periodicityFilter || deviationFilter}
                />
              );
            })}
        </ScrollableTable>

        <CheckboxListModal
          title={intl.formatMessage(messages.SelectOverviewColumns)}
          items={taskTemplates}
          onSave={this.handleColumnSelection}
          onClose={() => this.setState({ showColumnSelectionModal: false })}
          show={showColumnSelectionModal}
        />

        {showBulkModal && (
          <BulkUpdateDeviationsModal
            show={showBulkModal}
            onClose={() => this.setState({ showBulkModal: false })}
            onSuccess={this.onBulkSuccess}
            invoiceStreamIds={selectedIds}
            defaultMainTaskTemplateId={mainTaskTemplateId}
          />
        )}
      </div>
    );
  }
}

DeviationOverviewContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default showWhenPermission(
  'CapacityPlanning',
  'read',
)(injectStateService(injectIntl(DeviationOverviewContainer)));
